import type { ComponentProps, Dispatch, SetStateAction, SVGProps } from "react";
import { createContext, useContext, useState } from "react";
import { Link } from "@tanstack/react-router";
import rehypeTruncate from "rehype-truncate";
import { tv } from "tailwind-variants";

import {
  MESSAGE_TRUNCATE_LENGTH,
  MESSAGES_MARKDOWN_STYLES,
} from "@/domains/messages/models/constants";
import type { MessageDataModel } from "@/domains/messages/models/types";
import { focusRing, icons, MarkdownRenderer, Modal, pillToggle } from "@/ui";

const messageVariants = tv({
  slots: {
    root: "flex items-end gap-3 sm:gap-4",
    /*
      Keep every utility here unprefixed: call sites override max-width and
      padding, and twMerge cannot strip a breakpoint-prefixed class with a plain
      one — a leftover `sm:max-w-*` collapses the bubble to min-content.
    */
    bubble:
      "relative max-w-[85%] rounded-2xl bg-surface p-4 wrap-break-word text-content-secondary shadow-card",
    bubbleArrow: "absolute bottom-0",
    avatar:
      "flex aspect-square size-10 shrink-0 items-center justify-center rounded-full border sm:size-11",
    time: "mt-2 block text-sm",
    toggle: "size-4 shrink-0 transition-transform duration-300",
  },
  variants: {
    isOwnMessage: {
      true: {
        root: "flex-row-reverse",
        avatar: "border-line-strong bg-surface-sunken text-content",
        bubble: "bg-accent text-accent-content",
        bubbleArrow: "-right-2.5 -scale-x-100 transform text-accent",
        time: "text-accent-content/75",
      },
      false: {
        /* The clinician avatar carries EO blue so it stays legible on every surface. */
        avatar: "border-accent-line bg-accent-surface text-accent",
        bubbleArrow: "-left-2.5 text-surface",
        time: "text-content-muted",
      },
    },
  },
});

const { avatar, bubble, bubbleArrow, root, time: timeSlot, toggle } = messageVariants();

export type MessageProps = {
  message: MessageDataModel;
  truncate?: boolean;
} & ComponentProps<"div">;
type MessageContext = {
  isExpanded: boolean;
  setIsExpanded: Dispatch<SetStateAction<boolean>>;
} & MessageDataModel;

const MessageContext = createContext<MessageContext | null>(null);

export const useMessageContext = () => {
  const message = useContext(MessageContext);
  if (!message) {
    throw Error("useMessageContext can only be used inside a MessageContext.Provider");
  }

  return message;
};

const BubbleArrow = (props: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      fill="none"
      height="15"
      viewBox="0 0 14 15"
      width="14"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M6.76 15C9.3 15 11.62 13.6301 13.38 11.3805V0.57959L10 0.579596C10 8.5397 5.52 15 0 15H6.76Z"
        fill="currentColor"
      />
    </svg>
  );
};

type BubbleProps = {
  arrow?: boolean;
  time?: boolean;
} & ComponentProps<"div">;

const Bubble = ({ arrow = true, className, time = false }: BubbleProps) => {
  const message = useMessageContext();

  return (
    <div className={bubble({ className, isOwnMessage: message.isOwnMessage })}>
      <MarkdownRenderer
        components={MESSAGES_MARKDOWN_STYLES}
        rehypePlugins={[
          [rehypeTruncate, { maxChars: MESSAGE_TRUNCATE_LENGTH, disable: message.isExpanded }],
        ]}
      >
        {message.content}
      </MarkdownRenderer>

      {time ? (
        <time className={timeSlot({ isOwnMessage: message.isOwnMessage })}>
          {message.displayCreatedAtTime}
        </time>
      ) : null}

      <BubbleArrow
        className={bubbleArrow({
          className: arrow ? "" : "hidden",
          isOwnMessage: message.isOwnMessage,
        })}
      />
    </div>
  );
};

const Avatar = ({ placeholderInitials }: { placeholderInitials?: string }) => {
  const message = useMessageContext();

  return (
    <div className={avatar({ isOwnMessage: message.isOwnMessage })}>
      {message.isOwnMessage ? (
        <span className="text-sm font-semibold">{placeholderInitials ?? "-"}</span>
      ) : (
        <icons.Stethoscope aria-hidden="true" className="size-6" />
      )}
    </div>
  );
};

const Toggle = () => {
  const { isExpanded, setIsExpanded } = useMessageContext();

  const handleToggle = () => {
    return setIsExpanded((prev) => {
      return !prev;
    });
  };

  return (
    <button
      className={`flex w-fit cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 font-semibold text-content-secondary transition-colors hover:text-accent ${focusRing}`}
      onClick={handleToggle}
      type="button"
    >
      <span className="underline underline-offset-4">{isExpanded ? "View Less" : "View More"}</span>

      <icons.ChevronDoubleDownIcon
        aria-hidden="true"
        className={toggle({ className: isExpanded && "rotate-180" })}
      />
    </button>
  );
};

const Detail = () => {
  const { content, displayCreatedAtDate, displayCreatedAtTime } = useMessageContext();
  const [showModal, setShowModal] = useState(false);
  const handleOpen = () => {
    return setShowModal(true);
  };

  return (
    <>
      <Modal
        closeModal={() => {
          return setShowModal(false);
        }}
        isOpen={showModal}
      >
        <div className="flex flex-col">
          <div className="border-b p-4 text-sm font-semibold text-content-secondary">
            Latest message
          </div>

          <div className="max-h-96 flex-1 overflow-scroll px-6 py-3">
            <div className="mb-2 flex items-center gap-1 text-sm text-content-muted">
              <span className="font-semibold">{displayCreatedAtDate}</span>

              <span className="font-semibold">•</span>
              <span>{displayCreatedAtTime}</span>
            </div>

            <div className="text-content-secondary">
              <MarkdownRenderer components={MESSAGES_MARKDOWN_STYLES}>{content}</MarkdownRenderer>
            </div>
          </div>

          <div className="border-t p-3">
            <Link
              className={`flex min-h-11 items-center justify-center gap-3 rounded-md font-semibold text-content-secondary transition-colors hover:text-accent ${focusRing}`}
              to="/messages"
            >
              <span className="underline underline-offset-4">Reply in Message Center</span>

              <icons.ArrowRight aria-hidden="true" className="size-6" />
            </Link>
          </div>
        </div>
      </Modal>

      <button className={`${pillToggle} ml-auto`} onClick={handleOpen} type="button">
        Read more
        <icons.ArrowRight aria-hidden="true" className="size-4 shrink-0" />
      </button>
    </>
  );
};

const Root = ({ children, className, message, truncate = false }: MessageProps) => {
  const [isExpanded, setIsExpanded] = useState(!truncate);

  return (
    <MessageContext.Provider value={{ ...message, isExpanded, setIsExpanded }}>
      <div className={root({ className, isOwnMessage: message.isOwnMessage })}>{children}</div>
    </MessageContext.Provider>
  );
};

export const Message = {
  Root,
  Avatar,
  Bubble,
  Toggle,
  Detail,
};
